import { useCallback, useRef, useState } from 'react';

/** One change on the seating board, and how to take it back and do it again. */
export interface SeatingHistoryEntry {
  /** What the change was, as the "Undo …" notice names it. */
  label: string;
  undo: () => Promise<unknown>;
  redo: () => Promise<unknown>;
}

const LIMIT = 50;

/**
 * The seating board's undo and redo for this session. Each change is written
 * to the server as it is made, so undoing one writes what was there before —
 * an entry carries both writes. One runs at a time; an undo that fails stays
 * where it was, so it can be tried again.
 */
export function useSeatingHistory() {
  const [past, setPast] = useState<SeatingHistoryEntry[]>([]);
  const [future, setFuture] = useState<SeatingHistoryEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const running = useRef(false);

  const record = useCallback((entry: SeatingHistoryEntry) => {
    setPast((stack) => [...stack.slice(-(LIMIT - 1)), entry]);
    setFuture([]);
  }, []);

  const step = useCallback(
    async (direction: 'undo' | 'redo'): Promise<SeatingHistoryEntry | null> => {
      if (running.current) return null;
      const from = direction === 'undo' ? past : future;
      const entry = from[from.length - 1];
      if (!entry) return null;
      running.current = true;
      setBusy(true);
      try {
        await entry[direction]();
        if (direction === 'undo') {
          setPast((stack) => stack.slice(0, -1));
          setFuture((stack) => [...stack, entry]);
        } else {
          setFuture((stack) => stack.slice(0, -1));
          setPast((stack) => [...stack, entry]);
        }
        return entry;
      } finally {
        running.current = false;
        setBusy(false);
      }
    },
    [past, future],
  );

  const undo = useCallback(() => step('undo'), [step]);
  const redo = useCallback(() => step('redo'), [step]);

  const reset = useCallback(() => {
    setPast([]);
    setFuture([]);
  }, []);

  return {
    record,
    undo,
    redo,
    reset,
    busy,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    nextUndo: past[past.length - 1]?.label ?? null,
  };
}
